import React, { Component } from "react"
import PropTypes from "prop-types"
import withQueryParams from "src/hocs/withQueryParams"
import { compose } from "recompact"
import storage from "src/helpers/storage"
import moment from "moment"
import hocInterface from "src/helpers/hocInterface"
import {
  DEFAULT_SCALE,
  SCALE_CODES,
  SCALE_FROM_CODE,
  URL_DATE_FORMAT,
  LAST_SELECTED_DATES,
  LAST_SCROLL_POSITIONS,
} from "../constants"

/*
 * FIlls the gaps in saved scale dates
 * Ensures date restoration for all scales when scale is changed by controls
 * */
/* eslint-disable no-param-reassign */
const fillMissingDates = (savedDates, scale, selectedDate) => {
  switch (scale) {
    case `day`:
      savedDates.month = savedDates.day
      savedDates.week = savedDates.week || savedDates.day
      break
    case `week`:
      savedDates.month = savedDates.month || savedDates.week
      savedDates.day = savedDates.day || savedDates.week
      break
    case `month`:
      savedDates.day = savedDates.day || savedDates.month
      savedDates.week = savedDates.week || savedDates.month
      break
    default:
      // year
      savedDates.month =
        savedDates.month ||
        moment(selectedDate, URL_DATE_FORMAT)
          .startOf(`month`)
          .format(URL_DATE_FORMAT)
  }
}
/* eslint-enable no-param-reassign */

const timeChangeState = BaseComponent =>
  class TimeChangeState extends Component {
    constructor(props) {
      super(props)

      this.lastBoundedTime = {
        start: null,
        end: null,
      }

      this.displayTimeRange = {
        start: undefined,
        end: undefined,
      }

      this.disableVisibleTimeStart = () => {
        this.data.visibleTimeStart = undefined
        this.data.visibleTimeEnd = undefined
      }

      this.changeTime = (start, end, scale) => {
        this.data.visibleTimeStart = parseInt(start, 10)
        this.data.visibleTimeEnd = parseInt(end, 10)

        this.setCalendarTime(start, end)

        if (scale) {
          this.data.calendarScale = scale
        }
      }

      this.onBoundedTimeChange = (start, end) => {
        this.lastBoundedTime.start = Math.floor(start)
        this.lastBoundedTime.end = Math.floor(end)
      }

      this.data = {
        disableVisibleTimeStart: this.disableVisibleTimeStart,
        onBoundedTimeChange: this.onBoundedTimeChange,
      }

      this.updateTime(this.props)
    }

    // eslint-disable-next-line camelcase, react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
      const { props } = this
      if (
        props.queryParams !== nextProps.queryParams ||
        props.plan !== nextProps.plan
      ) {
        this.updateTime(nextProps)
      }
    }

    componentWillUnmount() {
      storage.remove(LAST_SCROLL_POSITIONS)
      storage.remove(LAST_SELECTED_DATES)
    }

    setCalendarTime(start, end) {
      this.data.calendarStartTime = parseInt(start, 10)
      this.data.calendarEndTime = parseInt(end, 10)
      this.data.momentCalendarStartTime = moment(this.data.calendarStartTime)
      this.data.momentCalendarEndTime = moment(this.data.calendarEndTime)
    }

    updateTime({ date, scale = SCALE_FROM_CODE[DEFAULT_SCALE] }) {
      const parsedDate = moment(date, URL_DATE_FORMAT)
      const numericStart = parsedDate.startOf(scale).valueOf()
      const numericEnd = parsedDate.endOf(scale).valueOf()

      const savedScroll = storage.get(LAST_SCROLL_POSITIONS)
      const savedDates = storage.get(LAST_SELECTED_DATES) || {}

      let calendarStartTime
      let calendarEndTime

      const defaultScaleName = SCALE_CODES[DEFAULT_SCALE]

      const restoredScroll = savedScroll ? savedScroll[scale] : null
      const restoredDate = savedDates ? savedDates[scale] : null

      savedDates[scale] = date
      fillMissingDates(savedDates, scale, date)

      /* Here we set "real" start and end points for timeline. First it tries to set
       * explicit start and end (by selected date or from local storage), if they don't exist - start point of a plan
       * will be used, if plan doesn't exist too - we will use today's date as a start point
       * */
      if (numericStart && numericEnd) {
        if (restoredScroll && restoredDate === date) {
          calendarStartTime = restoredScroll.start
          calendarEndTime = restoredScroll.end
        } else {
          calendarStartTime = moment(numericStart).valueOf()
          calendarEndTime = moment(numericEnd).valueOf()
        }
      } else {
        calendarStartTime = moment()
          .startOf(defaultScaleName)
          .valueOf()
        calendarEndTime = moment()
          .endOf(defaultScaleName)
          .valueOf()
      }

      storage.set(LAST_SELECTED_DATES, savedDates)

      // Set numeric timeline scale representaion (explicitly set or default) for further calculations
      const calendarScale = scale ? SCALE_FROM_CODE[scale] : DEFAULT_SCALE

      this.changeTime(calendarStartTime, calendarEndTime, calendarScale)
    }

    render() {
      return <BaseComponent {...this.props} {...this.data} />
    }
  }

export default hocInterface(
  {
    injectProps: {
      date: PropTypes.string.isRequired,
      scale: PropTypes.string.isRequired,
      plan: PropTypes.any,
    },
    exposeProps: [
      `calendarStartTime`,
      `calendarEndTime`,
      `calendarScale`,
      `visibleTimeStart`,
      `visibleTimeEnd`,
      `disableVisibleTimeStart`,
      `onBoundedTimeChange`,
      `momentCalendarStartTime`,
      `momentCalendarEndTime`,
    ],
  },
  compose(withQueryParams(), timeChangeState),
)
